import { functions } from '../firebase-config';
import { httpsCallable } from 'firebase/functions';

// Initialize Firebase Functions
const processDocumentOCR = httpsCallable(functions, 'processDocumentOCR');
const analyzeProperty = httpsCallable(functions, 'analyzeProperty');
const generateAIRendering = httpsCallable(functions, 'generateAIRendering');

/**
 * Process document with OCR and fraud detection
 * @param {string} fileUrl - URL of the uploaded document
 * @param {string} fileName - Name of the file
 * @param {string} propertyId - ID of the property
 * @returns {Promise<Object>} OCR results with extracted data and fraud analysis
 */
export async function processDocument(fileUrl, fileName, propertyId) {
  try {
    const result = await processDocumentOCR({
      fileUrl,
      fileName,
      propertyId
    });
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Document processing error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Analyze property for investment potential and market value
 * @param {string} propertyId - ID of the property
 * @param {Object} propertyData - Property details
 * @returns {Promise<Object>} Analysis results with price recommendations and investment score
 */
export async function analyzePropertyValue(propertyId, propertyData) {
  try {
    const result = await analyzeProperty({
      propertyId,
      propertyData
    });
    
    return {
      success: true,
      analysis: result.data.analysis
    };
  } catch (error) {
    console.error('Property analysis error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate 3D model for property
 * @param {string} propertyId - ID of the property
 * @param {Object} parameters - Model generation parameters
 * @returns {Promise<Object>} 3D model URL and specifications
 */
export async function generate3DModel(propertyId, parameters = {}) {
  try {
    const result = await generateAIRendering({
      propertyId,
      renderingType: '3dmodel',
      parameters
    });
    
    return {
      success: true,
      model: result.data.result
    };
  } catch (error) {
    console.error('3D model generation error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate floor plan using AI
 * @param {string} propertyId - ID of the property
 * @param {Object} parameters - Floor plan parameters
 * @returns {Promise<Object>} Floor plan URL and specifications
 */
export async function generateFloorPlan(propertyId, parameters = {}) {
  try {
    const result = await generateAIRendering({
      propertyId,
      renderingType: 'floorplan',
      parameters
    });
    
    return {
      success: true,
      floorPlan: result.data.result
    };
  } catch (error) {
    console.error('Floor plan generation error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate interior renderings
 * @param {string} propertyId - ID of the property
 * @param {Object} parameters - Rendering parameters (style, lighting, etc.)
 * @returns {Promise<Object>} Interior rendering URLs
 */
export async function generateInteriorRendering(propertyId, parameters = {}) {
  try {
    const result = await generateAIRendering({
      propertyId,
      renderingType: 'interior',
      parameters
    });
    
    return {
      success: true,
      renderings: result.data.result.renderings
    };
  } catch (error) {
    console.error('Interior rendering error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate virtual tour
 * @param {string} propertyId - ID of the property
 * @param {Object} parameters - Tour parameters
 * @returns {Promise<Object>} Virtual tour URL and specifications
 */
export async function generateVirtualTour(propertyId, parameters = {}) {
  try {
    const result = await generateAIRendering({
      propertyId,
      renderingType: 'virtualtour',
      parameters
    });
    
    return {
      success: true,
      tour: result.data.result
    };
  } catch (error) {
    console.error('Virtual tour generation error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate AR/VR content
 * @param {string} propertyId - ID of the property
 * @param {Object} parameters - AR/VR parameters
 * @returns {Promise<Object>} AR/VR content URLs and QR codes
 */
export async function generateARVRContent(propertyId, parameters = {}) {
  try {
    const result = await generateAIRendering({
      propertyId,
      renderingType: 'arvr',
      parameters
    });
    
    return {
      success: true,
      content: result.data.result.content
    };
  } catch (error) {
    console.error('AR/VR generation error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Process multiple documents in batch
 * @param {Array} documents - Array of document objects with fileUrl, fileName, propertyId
 * @returns {Promise<Array>} Array of processing results
 */
export async function processDocumentsBatch(documents) {
  const results = [];
  
  for (const doc of documents) {
    const result = await processDocument(doc.fileUrl, doc.fileName, doc.propertyId);
    results.push({
      fileName: doc.fileName,
      ...result
    });
  }
  
  return results;
}

/**
 * Get comprehensive property analysis including OCR, market analysis, and AI recommendations
 * @param {string} propertyId - ID of the property
 * @param {Object} propertyData - Complete property data
 * @param {Array} documents - Array of uploaded documents
 * @returns {Promise<Object>} Complete analysis results
 */
export async function getCompletePropertyAnalysis(propertyId, propertyData, documents = []) {
  try {
    // Process documents first
    let documentResults = [];
    if (documents.length > 0) {
      documentResults = await processDocumentsBatch(documents);
    }
    
    // Analyze property value and investment potential
    const analysisResult = await analyzePropertyValue(propertyId, propertyData);
    
    return {
      success: true,
      documents: documentResults,
      analysis: analysisResult.success ? analysisResult.analysis : null,
      propertyId
    };
  } catch (error) {
    console.error('Complete analysis error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate all visualization content for a property
 * @param {string} propertyId - ID of the property
 * @param {Object} visualizationOptions - Options for different visualization types
 * @returns {Promise<Object>} All generated visualization content
 */
export async function generateAllVisualizations(propertyId, visualizationOptions = {}) {
  try {
    const results = {};
    
    // Generate 3D model
    if (visualizationOptions.include3D !== false) {
      const model3D = await generate3DModel(propertyId, visualizationOptions.model3D);
      if (model3D.success) results.model3D = model3D.model;
    }
    
    // Generate floor plan
    if (visualizationOptions.includeFloorPlan !== false) {
      const floorPlan = await generateFloorPlan(propertyId, visualizationOptions.floorPlan);
      if (floorPlan.success) results.floorPlan = floorPlan.floorPlan;
    }
    
    // Generate interior renderings
    if (visualizationOptions.includeInterior !== false) {
      const interior = await generateInteriorRendering(propertyId, visualizationOptions.interior);
      if (interior.success) results.interior = interior.renderings;
    }
    
    // Generate virtual tour
    if (visualizationOptions.includeVirtualTour !== false) {
      const tour = await generateVirtualTour(propertyId, visualizationOptions.virtualTour);
      if (tour.success) results.virtualTour = tour.tour;
    }
    
    // Generate AR/VR content
    if (visualizationOptions.includeARVR !== false) {
      const arvr = await generateARVRContent(propertyId, visualizationOptions.arvr);
      if (arvr.success) results.arvr = arvr.content;
    }
    
    return {
      success: true,
      visualizations: results
    };
  } catch (error) {
    console.error('Visualization generation error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}
